import moment from "moment"
import { useCallback, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { DUMMY_ITEM_LIST_SUMMARY_ROWS } from "../data/dummyItemListSummary"
import type {
  ExportOutputType,
  ItemListSummaryRow,
  ListItemSummaryFilter,
  SortKey,
  SortState
} from "../types/listItemSummary"
import { DEFAULT_FILTER } from "../types/listItemSummary"
import {
  exportItemListSummaryXlsx,
  printItemListSummaryHtml
} from "../utils/listItemSummaryExport.common"
import { exportItemListSummaryPdf } from "../utils/listItemSummaryPdf"
import {
  computeKpi,
  getSortValue,
  inventoryValue
} from "../utils/listItemSummaryHelpers"

function filterRows(all: ItemListSummaryRow[], filter: ListItemSummaryFilter) {
  const values = all.map(inventoryValue).sort((a, b) => b - a)
  const highValueThreshold = values[Math.floor(values.length * 0.2)] || 0
  const lowValueThreshold = values[Math.floor(values.length * 0.8)] || 0

  return all.filter((item) => {
    const itemValue = inventoryValue(item)
    const searchable =
      `${item.itemCode} ${item.itemName} ${item.category} ${item.location} ${item.stockStatus} ${item.movementStatus}`.toLowerCase()

    const locationMatch =
      filter.location === "All Locations" || item.location === filter.location
    const categoryMatch =
      filter.category === "All Categories" || item.category === filter.category
    const stockMatch =
      filter.stockStatus === "All" || item.stockStatus === filter.stockStatus
    const movementMatch =
      filter.movement === "All" || item.movementStatus === filter.movement

    const searchMatch =
      !filter.search.trim() ||
      searchable.includes(filter.search.trim().toLowerCase())

    let valueMatch = true
    if (filter.valueRange === "High Value Items")
      valueMatch = itemValue >= highValueThreshold
    if (filter.valueRange === "Medium Value Items")
      valueMatch = itemValue < highValueThreshold && itemValue > lowValueThreshold
    if (filter.valueRange === "Low Value Items")
      valueMatch = itemValue <= lowValueThreshold

    return (
      locationMatch &&
      categoryMatch &&
      stockMatch &&
      movementMatch &&
      searchMatch &&
      valueMatch
    )
  })
}

function sortRows(rows: ItemListSummaryRow[], sort: SortState): ItemListSummaryRow[] {
  const next = [...rows]
  next.sort((a, b) => {
    const va = getSortValue(a, sort.key)
    const vb = getSortValue(b, sort.key)
    if (typeof va === "string" && typeof vb === "string") {
      const c = va.localeCompare(vb)
      return sort.asc ? c : -c
    }
    const na = Number(va)
    const nb = Number(vb)
    if (na < nb) return sort.asc ? -1 : 1
    if (na > nb) return sort.asc ? 1 : -1
    return 0
  })
  return next
}

export function useItemListSummaryReport() {
  const [filter, setFilter] = useState<ListItemSummaryFilter>(DEFAULT_FILTER)
  const [sort, setSort] = useState<SortState>({ key: "name", asc: true })

  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [pendingExportType, setPendingExportType] =
    useState<ExportOutputType | null>(null)
  const [printBlocked, setPrintBlocked] = useState(false)

  const periodLabel = useMemo(
    () => moment(filter.periodMonth, "YYYY-MM").format("MMMM YYYY"),
    [filter.periodMonth]
  )

  const filteredRows = useMemo(
    () => filterRows(DUMMY_ITEM_LIST_SUMMARY_ROWS, filter),
    [filter]
  )

  const displayRows = useMemo(
    () => sortRows(filteredRows, sort),
    [filteredRows, sort]
  )

  const kpi = useMemo(() => computeKpi(filteredRows), [filteredRows])

  const toggleSort = useCallback((key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, asc: !prev.asc } : { key, asc: true }
    )
  }, [])

  const resetFilter = useCallback(() => {
    setFilter(DEFAULT_FILTER)
    setSort({ key: "name", asc: true })
    toast.success("Filter reset")
  }, [])

  const searchReport = useCallback(() => {
    toast.success("Report updated", { duration: 1500 })
  }, [])

  const openExportModal = useCallback((type: ExportOutputType) => {
    setPendingExportType(type)
    setPrintBlocked(false)
    setExportModalOpen(true)
  }, [])

  const closeExportModal = useCallback(() => {
    setExportModalOpen(false)
    setPendingExportType(null)
    setPrintBlocked(false)
  }, [])

  const executeExport = useCallback(
    (orientation: "portrait" | "landscape") => {
      const type = pendingExportType
      if (!type) return

      try {
        if (type === "xls") {
          exportItemListSummaryXlsx({
            periodLabel,
            rows: displayRows
          })
          toast.success("Excel exported")
          closeExportModal()
          return
        }
        if (type === "pdf") {
          void exportItemListSummaryPdf({
            periodLabel,
            rows: displayRows,
            orientation
          })
            .then(() => {
              toast.success("PDF exported")
              closeExportModal()
            })
            .catch((e: unknown) => {
              console.error(e)
              toast.error("Export failed")
            })
          return
        }
        if (type === "print") {
          const w = printItemListSummaryHtml({
            periodLabel,
            rows: displayRows,
            orientation
          })
          if (!w) {
            setPrintBlocked(true)
            return
          }
          closeExportModal()
        }
      } catch (e) {
        toast.error("Export failed")
        console.error(e)
      }
    },
    [pendingExportType, periodLabel, displayRows, closeExportModal]
  )

  return {
    filter,
    setFilter,
    sort,
    toggleSort,
    periodLabel,
    displayRows,
    filteredRows,
    kpi,
    resetFilter,
    searchReport,
    exportModalOpen,
    pendingExportType,
    printBlocked,
    openExportModal,
    closeExportModal,
    executeExport
  }
}
